<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="4" md="3" class="py-2 my-0">
        <v-autocomplete
          v-model="form.particular"
          :items="addons"
          outlined
          dense
          hide-details="auto"
          clearable
          multiple
          label="Particular"
          item-text="text"
          item-value="id"
        >
          <template v-slot:selection="{ item, index }">
            <div class="d-flex align-center">
              <v-chip v-if="index < 2" small class="mr-2" outlined>
                {{ item.text }}
              </v-chip>
              <span v-if="index === 2" class="text-caption ml-2">
                (+{{ form.particular.length - 2 }} others)
              </span>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="4" md="2" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>

      <v-col cols="4" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.status"
          :items="statuses"
          item-text="name"
          item-value="value"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Status"
        ></v-autocomplete>
      </v-col>

      <v-col cols="4" md="2" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="init()"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!studentInvoices.length"
          :loading="downloadLoader"
          class="accent white--text ml-2"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            v-model="selectedApplicants"
            :headers="addonProgrammeFeeHeaders"
            :items="studentInvoices"
            fixed-header
            item-key="id"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>

            <template #[`item.usn_id`]="{ item }">
              {{ item.usn_id || "-" }}
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip
                small
                :color="item.status === 'Completed' ? 'success' : 'warning'"
                dark
              >
                {{ item.status === "Completed" ? "Paid" : "Due" }}
              </v-chip>
            </template>

            <template #[`item.sac`]="{}">
              999299
            </template>

            <template #[`item.student_details.current_term_no`]="{ item }">
              {{ item.student_details?.current_term_no || 1 }}
            </template>

            <template #[`item.student_details.partner_id`]="{ item }">
              {{ getPartnerName(item.student_details?.partner_id) }}
            </template>

            <template #[`item.student_details.program_id`]="{ item }">
              {{ getProgramName(item.student_details?.program_id) }}
            </template>

            <template #[`item.receipt_number`]="{ item }">
              {{ item.receipt_number || "-" }}
            </template>

            <template #[`item.transaction_id`]="{ item }">
              {{ item.transaction_id || "-" }}
            </template>

            <template #[`item.transaction_date`]="{ item }">
              {{ item.transaction_date ? formatDate(item.transaction_date) : "-" }}
            </template>

            <template #[`item.mobile_no`]="{ item }">
              {{ item.mobile_no || "-" }}
            </template>

            <template #[`item.amount`]="{ item }">
              {{ Number(item.amount).toLocaleString("en-IN") }}
            </template>

            <template #[`item.program_add_ons`]="{ item }">
              <div class="d-flex align-center flex-wrap" style="gap: 4px;">
                <template v-if="getAddonNamesList(item.program_add_ons).length">
                  <v-chip small outlined color="primary">
                    {{
                      getAddonNamesList(item.program_add_ons)[0].length > 15
                        ? getAddonNamesList(item.program_add_ons)[0].substring(0, 15) + "..."
                        : getAddonNamesList(item.program_add_ons)[0]
                    }}
                  </v-chip>
                  <v-chip
                    v-if="getAddonNamesList(item.program_add_ons).length > 1"
                    small
                    color="grey"
                    text-color="white"
                    @click="openSplitDialog(item)"
                    style="cursor: pointer;"
                  >
                    +{{ getAddonNamesList(item.program_add_ons).length - 1 }} more
                  </v-chip>
                </template>
                <span v-else>-</span>
              </div>
            </template>

            <template #[`item.addon_split_action`]="{ item }">
              <v-btn
                v-if="item.addon_split && item.addon_split.length"
                icon
                small
                color="accent"
                @click="openSplitDialog(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <span v-else>-</span>
            </template>

            <template #[`item.action`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="edit(item)"
                :disabled="item.status === 'Paid'"
              >
                mdi-pencil
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="splitDialog.show" max-width="850px">
      <v-card>
        <v-card-title class="primary white--text py-3">
          Add-on Programme Details
          <v-spacer></v-spacer>
          <v-btn icon dark small @click="splitDialog.show = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <div class="d-flex align-center mb-4 ml-3" style="gap: 16px;">
            <div>
              <span class="text-caption grey--text">Name</span>
              <div class="text-body-2 font-weight-medium">
                {{ splitDialog.studentName }}
              </div>
            </div>
            <v-divider vertical></v-divider>
            <div>
              <span class="text-caption grey--text">USN</span>
              <div class="text-body-2 font-weight-medium">
                {{ splitDialog.usnId }}
              </div>
            </div>
            <v-divider vertical></v-divider>
            <div>
              <span class="text-caption grey--text">Total (₹)</span>
              <div class="text-body-2 font-weight-bold primary--text">
                ₹{{ Number(splitDialog.totalAmount).toLocaleString("en-IN") }}
              </div>
            </div>
          </div>

          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr class="secondary">
                  <th class="text-left black--text font-weight-bold">Add-on Name</th>
                  <th class="text-right black--text font-weight-bold">GST Type</th>
                  <th class="text-right black--text font-weight-bold">Base Amount (₹)</th>
                  <th class="text-right black--text font-weight-bold">
                    CGST ({{ splitDialog.splits[0]?.cgst || 0 }}%)
                  </th>
                  <th class="text-right black--text font-weight-bold">
                    SGST ({{ splitDialog.splits[0]?.sgst || 0 }}%)
                  </th>
                  <th class="text-right black--text font-weight-bold">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(split, index) in splitDialog.splits"
                  :key="index"
                >
                  <td>
                    <div class="text-caption font-weight-medium">
                      {{ split.name }}
                    </div>
                  </td>
                  <td class="text-right text-caption">
                    {{ split.gst_type === "inclusive" ? "Inclusive" : "Exclusive" }}
                  </td>
                  <td class="text-right text-caption font-weight-medium">
                    {{ formatAmount(computeSplit(split).baseAmount) }}
                  </td>
                  <td class="text-right text-caption" style="color: #e65100;">
                    {{ formatAmount(computeSplit(split).cgstAmount) }}
                  </td>
                  <td class="text-right text-caption" style="color: #e65100;">
                    {{ formatAmount(computeSplit(split).sgstAmount) }}
                  </td>
                  <td class="text-right text-caption font-weight-bold">
                    {{ formatAmount(computeSplit(split).total) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="background-color: #f5f5f5;">
                  <td colspan="2" class="text-caption font-weight-bold py-2">
                    Grand Total
                  </td>
                  <td class="text-right text-caption font-weight-bold py-2">
                    {{ formatAmount(grandTotals.baseAmount) }}
                  </td>
                  <td class="text-right text-caption font-weight-bold py-2" style="color: #e65100;">
                    {{ formatAmount(grandTotals.cgstAmount) }}
                  </td>
                  <td class="text-right text-caption font-weight-bold py-2" style="color: #e65100;">
                    {{ formatAmount(grandTotals.sgstAmount) }}
                  </td>
                  <td class="text-right text-caption font-weight-bold py-2 primary--text">
                    {{ formatAmount(grandTotals.total) }}
                  </td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-card-actions class="py-3 px-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="splitDialog.show = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { addonProgrammeFeeHeaders } from "@/constants/headers";

export default {
  data() {
    return {
      form: {},
      programs: [],
      partners: [],
      batches: [],
      statuses: [
        { id: "Completed", name: "Paid", value: "Completed" },
        { id: "Pending", name: "Due", value: "Pending" },
      ],
      loading: false,
      addonProgrammeFeeHeaders,
      studentInvoices: [],
      selectedApplicants: [],
      addons: [],
      addonFeeSchedules: [],
      rules,
      downloadLoader: false,
      requestPayment: {},
      amountChange: false,

      splitDialog: {
        show: false,
        splits: [],
        studentName: "",
        usnId: "",
        totalAmount: 0,
      },
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    grandTotals() {
      return this.splitDialog.splits.reduce(
        (acc, split) => {
          const computed = this.computeSplit(split);
          acc.baseAmount += computed.baseAmount;
          acc.cgstAmount += computed.cgstAmount;
          acc.sgstAmount += computed.sgstAmount;
          acc.gstAmount += computed.gstAmount;
          acc.total += computed.total;
          return acc;
        },
        { baseAmount: 0, cgstAmount: 0, sgstAmount: 0, gstAmount: 0, total: 0 }
      );
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Add-on Programme Fee" }]);
    await this.init();
  },

  methods: {
    async init() {
      await Promise.all([
        this.getAddons(),
        this.generateBatchList(),
        this.getPartners(),
        this.getPrograms(),
        this.getAddonFeeSchedules(),
        this.getAddonInvoices(),
      ]);
    },

    async generateBatchList() {
      try {
        const res = await this.$axios.get("/get_year_batch");
        this.batches = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    async getAddons() {
      try {
        const res = await this.$axios.get(`/program_addons`);
        const addons = res.data.data;
        this.addons = addons
          .sort((a, b) => a.id - b.id)
          .map((f) => ({ id: f.id, text: f.name }));
      } catch (error) {
        console.log(error);
      }
    },

    async getAddonFeeSchedules() {
      try {
        const res = await this.$axios.get(`/program_addon_fee_schedule`);
        this.addonFeeSchedules = res.data.data.sort((a, b) => a.id - b.id);
      } catch (error) {
        console.log(error);
      }
    },

    async getAddonInvoices() {
      this.loading = true;
      try {
        const queryString = this.objectToQueryParams({ ...this.form });
        const res = await this.$axios.get(`/get_all_addon_invoices?${queryString}`);
        this.studentInvoices = res.data.data.sort((a, b) => {
          const dateA = a.transaction_date
            ? new Date(a.transaction_date.split("-").reverse().join("-"))
            : null;
          const dateB = b.transaction_date
            ? new Date(b.transaction_date.split("-").reverse().join("-"))
            : null;
          return (dateA || 0) - (dateB || 0);
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async getPrograms() {
      try {
        const res = await this.$axios.get("/programs");
        this.programs = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("/partners");
        this.partners = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    roundToHalf(value) {
      return Math.round(value * 2) / 2;
    },

    computeSplit(split) {
      const amount = Number(split.amount);
      const cgst = Number(split.cgst || 0);
      const sgst = Number(split.sgst || 0);
      const totalGstPct = cgst + sgst;

      if (split.gst_type === "inclusive") {
        const base = amount / (1 + totalGstPct / 100);
        const rawCgst = base * (cgst / 100);
        const rawSgst = base * (sgst / 100);
        const cgstAmount = this.roundToHalf(rawCgst);
        const sgstAmount = this.roundToHalf(rawSgst);
        const baseAmount = Math.round((amount - cgstAmount - sgstAmount) * 100) / 100;

        return {
          baseAmount,
          cgstAmount,
          sgstAmount,
          gstAmount: Math.round((cgstAmount + sgstAmount) * 100) / 100,
          total: amount,
        };
      } else {
        const rawCgst = (amount * cgst) / 100;
        const rawSgst = (amount * sgst) / 100;

        const cgstAmount = this.roundToHalf(rawCgst);
        const sgstAmount = this.roundToHalf(rawSgst);
        const total = Math.round((amount + cgstAmount + sgstAmount) * 100) / 100;

        return {
          baseAmount: amount,
          cgstAmount,
          sgstAmount,
          gstAmount: Math.round((cgstAmount + sgstAmount) * 100) / 100,
          total,
        };
      }
    },

    formatAmount(value) {
      return `₹${Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    openSplitDialog(item) {
      this.splitDialog = {
        show: true,
        splits: item.addon_split || [],
        studentName: item.student_details?.name || "-",
        usnId: item.usn_id || "-",
        totalAmount: item.amount,
      };
    },

    objectToQueryParams(obj) {
      if (this.currentUser.partner_id != 1) {
        obj.partner_child_id = this.currentUser.partner_id;
      }

      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }

      if (obj.partner_id == 1) delete obj.partner_id;
      if (!obj?.types?.length) delete obj.types;

      if (obj.particular && Array.isArray(obj.particular) && obj.particular.length) {
        obj.particular = obj.particular.map((p) => p.id || p).join(",");
      } else {
        delete obj.particular;
      }

      if (obj.status && typeof obj.status === "object") {
        obj.status = obj.status.value;
      }

      return Object.keys(obj)
        .filter((key) => obj[key] !== undefined && obj[key] !== null && obj[key] !== "")
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
        .join("&");
    },

    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "-";
    },

    getPartnerName(id) {
      const partner = this.partners.find((p) => p.id === id);
      return partner ? partner.name : "-";
    },

    getAddonNamesList(ids) {
      if (!ids || !Array.isArray(ids) || ids.length === 0) return [];
      return ids
        .map((id) => {
          const addon = this.addons.find((a) => a.id === id);
          return addon?.text || null;
        })
        .filter(Boolean);
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    },

    async download() {
      this.downloadLoader = true;
      const queryString = this.objectToQueryParams({ ...this.form });
      try {
        const res = await this.$axios.get(
          `/download_addon_fee_report?${queryString}`,
          { responseType: "blob" }
        );
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Addon Fee Report.csv");
        document.body.appendChild(link);
        link.click();
        this.downloadLoader = false;
      } catch (error) {
        console.log(error);
        this.downloadLoader = false;
      }
    },

    edit(item) {
      this.requestPayment = { ...item };
      this.amountChange = true;
    },
  },
};
</script>

<style scoped>
::v-deep .v-data-table__wrapper tbody tr td {
  vertical-align: middle;
}

::v-deep .v-simple-table thead tr th {
  padding: 8px 12px !important;
}

::v-deep .v-simple-table tbody tr td {
  padding: 8px 12px !important;
}

::v-deep .v-simple-table tfoot tr td {
  padding: 8px 12px !important;
  border-top: 2px solid #e0e0e0;
}
</style>